import { ConfigurableProperty, DigitalTwin, DigitalTwinPropertyObject } from 'api/digitalTwin/digitalTwin.api'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'ui/components/Dialog/Dialog'
import { DigitalTwinSettingDialog } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialog'
import { CreateSettingData } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import { getEditorTitle, getSubmitLabel } from 'ui/components/DigitalTwinSettingEditor/editorTitle.helper'
import { SettingEditorBanners } from 'ui/components/DigitalTwinSettingEditor/SettingEditorBanners'
import { getSettingEditorState } from 'ui/components/DigitalTwinSettingEditor/settingEditorState.helper'
import { useConfirmedClose } from 'ui/components/DigitalTwinSettingEditor/useConfirmedClose'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import { UnitSettingFormData } from 'ui/components/UnitSettingForm/UnitSettingForm'

import { getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'

type CreateSettingModalProps = {
  defaultValues?: Partial<UnitSettingFormData>
  propertyItems: DigitalTwinPropertyObject[]
  digitalTwin: DigitalTwin
  onClose: () => void
  allowEmpty: boolean
  onSettingModified: () => void
  priorities: SystemSettingPriorityLevel[]
  baseSettingPriorities?: SystemSettingPriorityLevel[]
  isBaseSettings?: boolean
  isCollectedSettings?: boolean
  initData: null | CreateSettingData
  configurableProperties: ConfigurableProperty[]
}

export default function CreateSettingModal({
  initData,
  configurableProperties,
  propertyItems,
  digitalTwin,
  onClose,
  onSettingModified,
  priorities,
  baseSettingPriorities,
  isBaseSettings = false,
  isCollectedSettings = false,
}: CreateSettingModalProps): ReactElement {
  const { t } = useTranslation()
  const [copy, setCopy] = useState<CreateSettingData | null>(null)
  const { requestClose, onUnsavedChangesChange, closeConfirmDialog } = useConfirmedClose(onClose)

  if (!digitalTwin || !initData) {
    return <></>
  }

  const { mode, isTimeSeries, isEventSetting, isBaseSettingInCollectedSettings, isLocked } = getSettingEditorState({
    setting: initData,
    copy,
    isCollectedSettings,
  })

  const submitLabel = getSubmitLabel({ isTimeSeries, mode }, t)

  return (
    <Dialog
      fullWidth
      open={true}
      aria-labelledby="form-dialog-title"
      maxWidth="xl"
      onClose={requestClose}
      data-testid="deviation-setting-dialog" // e2e: settings dialog opened from the settings calendar — do not remove
    >
      <CustomDialogTitle
        title={getEditorTitle({
          propertyTitle: initData.name ? getPrettyName(initData.name, digitalTwin.translations) : null,
          editedSetting: { isTimeSeries, mode },
          t,
        })}
        handleClose={requestClose}
      />
      <div className="mx-5 empty:hidden">
        <SettingEditorBanners
          lacksTimeSeriesAccess={false}
          isEventSetting={isEventSetting}
          mode={mode}
          copyActionLabel={submitLabel}
          isBaseSettingInCollectedSettings={isBaseSettingInCollectedSettings}
        />
      </div>

      <DigitalTwinSettingDialog
        initData={initData}
        configurableProperties={configurableProperties}
        onClose={onClose}
        onSettingModified={onSettingModified}
        propertyItems={propertyItems}
        digitalTwin={digitalTwin}
        allowEmpty={false}
        priorities={priorities}
        baseSettingPriorities={baseSettingPriorities}
        isBaseSettings={isBaseSettings}
        isCollectedSettings={isCollectedSettings}
        hasUnitAndProperty={false}
        disable={isLocked}
        onCopyChange={setCopy}
        onUnsavedChangesChange={onUnsavedChangesChange}
        onRequestClose={requestClose}
        submitLabel={submitLabel}
      />
      {closeConfirmDialog}
    </Dialog>
  )
}
